/**
 * Letterhead writing module.
 *
 * Rich-text editor (Tiptap) → click Print → opens a print preview on the
 * UNIVERS COSMETIC letterhead (header + narrow margins + Google-review QR
 * top-right + legal footer at bottom, no page numbers, no thanks line).
 */
import { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { FontSize, LineHeight } from '../lib/tiptapExtensions';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
    Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
    AlignLeft, AlignCenter, AlignRight, AlignJustify, Heading1, Heading2,
    Quote, Link as LinkIcon, Undo, Redo, Printer, QrCode, Mail,
} from 'lucide-react';
import { printLetter } from '../lib/printLetter';

const ToolbarButton = ({ active, onClick, title, testId, children }) => (
    <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClick}
        title={title}
        data-testid={testId}
        className={`h-8 w-8 p-0 rounded ${active ? 'bg-[#C08478]/20 text-[#C08478]' : 'text-gray-600 hover:bg-gray-100'}`}
    >
        {children}
    </Button>
);

const Divider = () => <span className="w-px h-5 bg-gray-200 mx-1" />;

const DEFAULT_LETTER = `<p style="text-align:right;">Bobigny, le <strong>${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></p>
<p><br></p>
<p><strong>Destinataire</strong><br>Adresse ligne 1<br>Code postal Ville</p>
<p><br></p>
<p><strong>Objet :</strong> <em>(objet de la lettre)</em></p>
<p><br></p>
<p>Madame, Monsieur,</p>
<p><br></p>
<p>(Rédigez votre message ici…)</p>
<p><br></p>
<p>Nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.</p>
<p><br></p>
<p style="text-align:right;">La Direction<br><strong>SAS UNIVERS COSMETIC</strong></p>`;

const Letter = () => {
    const [subject, setSubject] = useState('');
    const [showQr, setShowQr] = useState(true);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Underline,
            TextStyle,
            FontFamily.configure({ types: ['textStyle'] }),
            FontSize,
            LineHeight,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
        ],
        content: DEFAULT_LETTER,
        editorProps: {
            attributes: {
                class: 'tiptap-editor min-h-[480px] focus:outline-none px-6 py-8 prose prose-sm max-w-none',
                'data-testid': 'letter-editor',
            },
        },
    });

    const handlePrint = () => {
        if (!editor) return;
        const html = editor.getHTML();
        if (!html || html === '<p></p>') {
            toast.error('Le contenu de la lettre est vide.');
            return;
        }
        printLetter({ bodyHtml: html, showQr });
    };

    const addLink = () => {
        const url = window.prompt('URL ?', 'https://');
        if (!url) return;
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    if (!editor) return null;

    return (
        <div data-testid="letter-page" className="space-y-6 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Lettre sur papier à entête</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Rédigez votre courrier puis imprimez-le sur le papier à entête UNIVERS COSMETIC.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200">
                        <QrCode size={14} className="text-[#C08478]" />
                        <Label htmlFor="qr-toggle" className="text-xs cursor-pointer">Show / Hide QR Code</Label>
                        <Switch id="qr-toggle" checked={showQr} onCheckedChange={setShowQr} data-testid="qr-toggle" />
                    </div>
                    <Button
                        onClick={handlePrint}
                        data-testid="print-letter-btn"
                        className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                    >
                        <Printer size={14} className="mr-2" />
                        Imprimer / PDF
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                    {/* Internal-only subject — not printed; used for testability */}
                    <div className="px-6 pt-5 pb-3 border-b border-gray-100 flex items-center gap-3">
                        <Mail size={14} className="text-[#C08478] shrink-0" />
                        <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Objet interne (ne s'imprime pas, juste pour vous repérer)"
                            data-testid="letter-internal-subject"
                            className="border-0 focus-visible:ring-0 px-0 text-sm"
                        />
                    </div>

                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-gray-100 bg-[#FBF8F6]">
                        {/* Font family */}
                        <Select
                            onValueChange={(v) => v === 'default' ? editor.chain().focus().unsetFontFamily().run() : editor.chain().focus().setFontFamily(v).run()}
                        >
                            <SelectTrigger className="h-8 w-[140px] text-xs bg-white" data-testid="tb-font-family">
                                <SelectValue placeholder="Police" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="default">Par défaut</SelectItem>
                                <SelectItem value="'Century Gothic','Manrope',sans-serif">Century Gothic</SelectItem>
                                <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                                <SelectItem value="'Times New Roman', serif">Times New Roman</SelectItem>
                                <SelectItem value="Georgia, serif">Georgia</SelectItem>
                                <SelectItem value="'Courier New', monospace">Courier New</SelectItem>
                                <SelectItem value="Verdana, sans-serif">Verdana</SelectItem>
                                <SelectItem value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</SelectItem>
                            </SelectContent>
                        </Select>
                        {/* Font size */}
                        <Select
                            onValueChange={(v) => v === 'default' ? editor.chain().focus().unsetFontSize().run() : editor.chain().focus().setFontSize(v).run()}
                        >
                            <SelectTrigger className="h-8 w-[90px] text-xs bg-white" data-testid="tb-font-size">
                                <SelectValue placeholder="Taille" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="default">Par défaut</SelectItem>
                                {['9px','10px','11px','12px','13px','14px','16px','18px','20px','24px','28px','32px'].map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {/* Line height */}
                        <Select
                            onValueChange={(v) => v === 'default' ? editor.chain().focus().unsetLineHeight().run() : editor.chain().focus().setLineHeight(v).run()}
                        >
                            <SelectTrigger className="h-8 w-[110px] text-xs bg-white" data-testid="tb-line-height">
                                <SelectValue placeholder="Interligne" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="default">Par défaut</SelectItem>
                                <SelectItem value="1">Serré (1.0)</SelectItem>
                                <SelectItem value="1.15">Compact (1.15)</SelectItem>
                                <SelectItem value="1.5">Normal (1.5)</SelectItem>
                                <SelectItem value="1.75">Aéré (1.75)</SelectItem>
                                <SelectItem value="2">Double (2.0)</SelectItem>
                                <SelectItem value="2.5">Très aéré (2.5)</SelectItem>
                            </SelectContent>
                        </Select>
                        <Divider />
                        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras (Ctrl+B)" testId="tb-bold"><Bold size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique (Ctrl+I)" testId="tb-italic"><Italic size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Souligné (Ctrl+U)" testId="tb-underline"><UnderlineIcon size={15} /></ToolbarButton>
                        <Divider />
                        <ToolbarButton active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Titre 1" testId="tb-h1"><Heading1 size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre 2" testId="tb-h2"><Heading2 size={15} /></ToolbarButton>
                        <Divider />
                        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces" testId="tb-ul"><List size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée" testId="tb-ol"><ListOrdered size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation" testId="tb-quote"><Quote size={15} /></ToolbarButton>
                        <Divider />
                        <ToolbarButton active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Aligner à gauche" testId="tb-align-left"><AlignLeft size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Centrer" testId="tb-align-center"><AlignCenter size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Aligner à droite" testId="tb-align-right"><AlignRight size={15} /></ToolbarButton>
                        <ToolbarButton active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justifier" testId="tb-align-justify"><AlignJustify size={15} /></ToolbarButton>
                        <Divider />
                        <ToolbarButton active={editor.isActive('link')} onClick={addLink} title="Insérer un lien" testId="tb-link"><LinkIcon size={15} /></ToolbarButton>
                        <Divider />
                        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Annuler" testId="tb-undo"><Undo size={15} /></ToolbarButton>
                        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Rétablir" testId="tb-redo"><Redo size={15} /></ToolbarButton>
                    </div>

                    {/* Editor surface — mocks the look of an A4 page */}
                    <div className="bg-[#F5F5F5] p-6">
                        <div className="mx-auto bg-white shadow-sm" style={{ width: '210mm', maxWidth: '100%', minHeight: '297mm' }}>
                            <EditorContent editor={editor} />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Letter;
